import React from 'react';
import { Button } from './ui/button';
import { Phone, Mail, ArrowRight, CheckCircle } from 'lucide-react';
import { businessInfo, translations } from '../data/mock';

const HeroSection = ({ onContactClick }) => {
  const t = translations.hero;

  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-gray-200/30 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-0 w-72 h-72 bg-gray-300/20 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-gray-900/5 rounded-full px-4 py-2">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-gray-700">{t.badge}</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              {t.headline1}
              <span className="block mt-2 bg-gradient-to-r from-gray-900 via-gray-700 to-gray-500 bg-clip-text text-transparent">
                {t.headline2}
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-gray-600 max-w-xl leading-relaxed">
              {t.subheadline}
              <span className="font-semibold text-gray-900"> {t.diagnosticsFree}</span>
            </p>

            {/* Trust Points */}
            <div className="flex flex-wrap gap-4">
              {t.trustPoints.map((point) => (
                <div key={point} className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm font-medium">{point}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col gap-4">
              <Button
                onClick={onContactClick}
                size="lg"
                className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-6 text-lg group w-full sm:w-auto"
              >
                {t.ctaPrimary}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={`tel:${businessInfo.phone}`}
                  className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all w-full sm:w-auto"
                >
                  <Phone className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-700 whitespace-nowrap">
                    {t.ctaCall}
                  </span>
                </a>

                <a
                  href={`mailto:${businessInfo.email}`}
                  className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all w-full sm:w-auto"
                >
                  <Mail className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-700 whitespace-nowrap">
                    {t.ctaEmail}
                  </span>
                </a>
              </div>
            </div>

          </div>

          {/* Right Content - Visual */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Main Card */}
              <div className="bg-white rounded-3xl shadow-2xl shadow-gray-200/50 p-8 transform rotate-2 hover:rotate-0 transition-transform duration-500">
                <div className="space-y-6">
                  {/* Device Icons */}
                  <div className="flex justify-center gap-8">
                    <div className="w-24 h-40 bg-gray-100 rounded-2xl flex items-center justify-center">
                      <div className="w-16 h-28 bg-gray-900 rounded-xl relative">
                        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-gray-700 rounded-full" />
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 border-2 border-gray-700 rounded-full" />
                      </div>
                    </div>
                    <div className="w-40 h-28 bg-gray-100 rounded-2xl flex items-center justify-center">
                      <div className="w-32 h-20 bg-gray-900 rounded-lg relative">
                        <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-24 h-4 bg-gray-700 rounded-b-lg" />
                      </div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 rounded-full px-4 py-2">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                      <span className="text-sm font-medium">Diagnostika: {t.free}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -left-4 bg-white rounded-2xl shadow-xl p-4 animate-bounce" style={{ animationDuration: '3s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t.priceEstimate}</p>
                    <p className="text-sm font-semibold text-gray-900">{t.transparent}</p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-gray-900">0</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t.diagnosticsCost}</p>
                    <p className="text-sm font-semibold text-emerald-600">{t.free}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
